import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import StatusBadge from "@/components/StatusBadge";
import { Download, FileCheck2, AlertTriangle, Save, X, Wand2, Loader2, ChevronDown, ArrowLeft, Paperclip, Image as ImageIcon, Table as TableIcon, BarChart3, Trash2, ArrowUp, ArrowDown, Plus, UploadCloud, LayoutTemplate, List } from "lucide-react";
import { AttachmentBlock, CoverPreview, ContentsPreview, TypeIcon } from "@/components/DocBlocks";

const regenPresets = [
  { k:"improve_clarity", label:"Improve clarity"},
  { k:"concise", label:"Make concise"},
  { k:"technical", label:"Technical language"},
  { k:"plain", label:"Plain English"},
  { k:"preserve", label:"Preserve wording"},
];

export default function DocumentDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { activeWorkspace, canEdit } = useAuth();
  const [doc, setDoc] = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState("");
  const [busy, setBusy] = useState(false);
  const [showRegen, setShowRegen] = useState(false);
  const [showMeta, setShowMeta] = useState(false);
  const [template, setTemplate] = useState(null);
  const [showAttach, setShowAttach] = useState(null); // "image" | "table" | "chart" | null
  const [selectedBlock, setSelectedBlock] = useState(null);

  const attachments = doc?.attachments || [];
  const sectionsList = doc?.content_json?.sections || [];
  const coverMode = doc?.settings?.cover_page ?? template?.cover_page ?? "none";
  const contentsMode = doc?.settings?.contents_page ?? template?.contents_page ?? "none";

  const setPageSetting = async (key, value) => {
    try {
      const { data } = await api.patch(`/workspaces/${activeWorkspace.id}/documents/${id}`, { settings: { [key]: value } });
      setDoc((d) => ({ ...d, settings: data.settings }));
    } catch { toast.error("Could not update page setup"); }
  };

  const updateAttachment = async (attId, patch) => {
    setDoc((d) => ({ ...d, attachments: (d.attachments || []).map((a) => (a.id === attId ? { ...a, ...patch } : a)) }));
    try {
      await api.patch(`/workspaces/${activeWorkspace.id}/documents/${id}/attachments/${attId}`, patch);
    } catch { toast.error("Could not update block"); refreshAttachments(); }
  };

  const refreshAttachments = async () => {
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspace.id}/documents/${id}/attachments`);
      setDoc((d) => ({ ...d, attachments: data }));
    } catch {}
  };

  const deleteAttachment = async (attId) => {
    if (!window.confirm("Remove this attachment?")) return;
    try {
      await api.delete(`/workspaces/${activeWorkspace.id}/documents/${id}/attachments/${attId}`);
      setDoc((d) => ({ ...d, attachments: (d.attachments || []).filter((a) => a.id !== attId) }));
      toast.success("Attachment removed");
    } catch { toast.error("Could not remove"); }
  };

  const moveAttachment = async (attId, dir) => {
    const idx = attachments.findIndex((a) => a.id === attId);
    if (idx < 0) return;
    const j = idx + dir;
    if (j < 0 || j >= attachments.length) return;
    const next = attachments.slice();
    [next[idx], next[j]] = [next[j], next[idx]];
    setDoc((d) => ({ ...d, attachments: next }));
    try {
      await api.patch(`/workspaces/${activeWorkspace.id}/documents/${id}/attachments/reorder`, { ids: next.map((a) => a.id) });
    } catch { toast.error("Could not reorder"); refreshAttachments(); }
  };

  const load = async () => {
    const { data } = await api.get(`/workspaces/${activeWorkspace.id}/documents/${id}`);
    setDoc(data);
    if (data.content_json?.sections?.length && !activeSection) {
      setActiveSection(data.content_json.sections[0].id);
    }
    if (data.template_id) {
      try { const t = await api.get(`/workspaces/${activeWorkspace.id}/templates/${data.template_id}`); setTemplate(t.data); } catch {}
    }
  };
  useEffect(() => { if (activeWorkspace) load(); /* eslint-disable-next-line */ }, [activeWorkspace, id]);

  const sections = doc?.content_json?.sections || [];
  const reviewItems = doc?.content_json?.review_items || [];
  const current = useMemo(() => sections.find(s => s.id === activeSection) || sections[0], [sections, activeSection]);

  // Figure/Table numbering mirrors the export order: per-section, then unplaced.
  const numbering = useMemo(() => {
    const map = {}; let fig = 0, tbl = 0;
    const bump = (a) => { if (a.type === "table") { tbl += 1; map[a.id] = tbl; } else { fig += 1; map[a.id] = fig; } };
    sections.forEach((s) => attachments.filter((a) => a.section_id === s.id).forEach(bump));
    attachments.filter((a) => !a.section_id).forEach(bump);
    return map;
  }, [sections, attachments]);

  const renderBlock = (b, i) => {
    if (b.type === "bullet_list") return <ul key={i}>{(b.items||[]).map((x,j)=><li key={j}>{x}</li>)}</ul>;
    if (b.type === "numbered_list") return <ol key={i}>{(b.items||[]).map((x,j)=><li key={j}>{x}</li>)}</ol>;
    return <p key={i}>{b.text}</p>;
  };

  const startEdit = () => {
    if (!current) return;
    const text = (current.content || []).map(b => b.type === "paragraph" ? b.text : (b.items||[]).join("\n")).join("\n\n");
    setEditText(text); setEditing(true);
  };
  const saveEdit = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/workspaces/${activeWorkspace.id}/documents/${id}/sections`, { section_id: current.id, content: editText });
      setDoc((d) => ({ ...d, content_json: data }));
      setEditing(false); toast.success("Saved");
    } catch { toast.error("Could not save"); }
    finally { setBusy(false); }
  };

  const regenerate = async (preset) => {
    setBusy(true); setShowRegen(false);
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspace.id}/documents/${id}/sections/regenerate`, { section_id: current.id, instruction: preset });
      setDoc((d) => {
        const cj = { ...d.content_json };
        cj.sections = cj.sections.map((s) => s.id === current.id ? data : s);
        return { ...d, content_json: cj };
      });
      toast.success("Section regenerated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not regenerate"); }
    finally { setBusy(false); }
  };

  const resolveItem = async (section_id, resolution) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspace.id}/documents/${id}/resolve`, { section_id, resolution });
      setDoc((d) => ({ ...d, content_json: data }));
    } finally { setBusy(false); }
  };

  const finalize = async () => {
    await api.post(`/workspaces/${activeWorkspace.id}/documents/${id}/finalize`);
    toast.success("Marked final"); load();
  };

  const download = async () => {
    const token = localStorage.getItem("fx_token");
    const url = `${BACKEND_URL}/api/workspaces/${activeWorkspace.id}/documents/${id}/export`;
    const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` }});
    if (!r.ok) { toast.error("Could not download"); return; }
    const blob = await r.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = `${doc.document_number || "document"}.docx`; a.click();
  };

  if (!doc) return <div className="p-10 text-slate-400" data-testid="doc-loading">Loading document…</div>;

  return (
    <div className="flex flex-col h-screen" data-testid="doc-detail">
      <div className="border-b border-[#1E293B] bg-[#070B18] px-5 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={()=>nav("/app/documents")} className="text-slate-400 hover:text-white p-1" data-testid="back-btn"><ArrowLeft size={16}/></button>
          <div className="min-w-0">
            <div className="text-sm font-medium text-slate-100 truncate">{doc.title}</div>
            <div className="text-[11px] mono text-slate-500 uppercase tracking-wider">{doc.document_number} · Rev {doc.revision} · {doc.document_type}</div>
          </div>
          <StatusBadge status={doc.status}/>
        </div>
        <div className="flex items-center gap-2">
          {!canEdit && <span className="text-[10px] mono uppercase tracking-widest px-2 py-1 rounded border border-slate-700 text-slate-400" data-testid="viewer-badge">Read-only</span>}
          {canEdit && doc.status !== "final" && <button onClick={finalize} className="border border-emerald-500/30 hover:bg-emerald-500/10 text-emerald-300 px-3 py-1.5 rounded-lg text-sm inline-flex items-center gap-1" data-testid="finalize-btn"><FileCheck2 size={14}/> Mark final</button>}
          <button onClick={download} className="fx-btn-primary text-white px-4 py-1.5 rounded-lg text-sm font-medium inline-flex items-center gap-1" data-testid="download-btn"><Download size={14}/> Export .docx</button>
        </div>
      </div>

      <div className="grid grid-cols-12 flex-1 min-h-0">
        <aside className="col-span-3 border-r border-[#1E293B] bg-[#0A0F1F] overflow-y-auto p-3 fx-scrollbar hidden lg:block" data-testid="left-sections">
          <div className="mono text-[10px] uppercase tracking-widest text-slate-500 px-2 mb-2">Sections</div>
          {sections.map((s, i) => (
            <button key={s.id} onClick={()=>{setActiveSection(s.id); setEditing(false);}}
              className={`w-full text-left px-3 py-2 rounded-lg mb-1 text-sm transition flex items-center justify-between gap-2 ${activeSection===s.id ? "bg-white/5 text-white" : "text-slate-300 hover:bg-white/5"}`} data-testid={`section-${s.id}`}>
              <span className="flex items-center gap-2 min-w-0"><span className="mono text-slate-600 text-xs w-5">{String(i+1).padStart(2,"0")}</span><span className="truncate">{s.heading}</span></span>
              {s.status !== "complete" && <span className={`w-1.5 h-1.5 rounded-full ${s.status==="missing" ? "bg-red-400" : "bg-amber-400"}`}/>}
            </button>
          ))}
        </aside>

        <div className="col-span-12 lg:col-span-6 overflow-y-auto fx-scrollbar p-8 flex justify-center items-start bg-[#050816]">
          <div className="fx-a4">
            {coverMode === "standard" && <CoverPreview doc={doc} workspace={activeWorkspace} />}
            {contentsMode === "auto" && <ContentsPreview sections={sections} title={template?.contents_title || "Contents"} />}
            <div className="flex items-start justify-between border-b border-slate-200 pb-3 mb-3">
              <div>
                <div className="mono text-[9px] uppercase tracking-widest text-slate-500">{doc.document_type}</div>
                <h1 className="doc-title">{doc.title}</h1>
              </div>
              <div className="text-right text-xs text-slate-500">
                <div className="mono">{doc.document_number}</div>
                <div>Rev {doc.revision}</div>
              </div>
            </div>
            <table className="meta">
              <tbody>
                <tr>
                  <td className="k">Department</td><td>{doc.metadata?.department || "—"}</td>
                  <td className="k">Site</td><td>{doc.metadata?.site || "—"}</td>
                </tr>
                <tr>
                  <td className="k">Author</td><td>{doc.metadata?.author || "—"}</td>
                  <td className="k">Effective</td><td>{doc.metadata?.effective_date || "—"}</td>
                </tr>
              </tbody>
            </table>
            {sections.map((s, i) => (
              <div key={s.id}>
                <h2 className="section">{i+1}. {s.heading}</h2>
                {(!s.content || s.content.length === 0) ? (
                  <p style={{color:"#94a3b8", fontStyle:"italic"}}>[Awaiting content — this section was flagged for review]</p>
                ) : s.content.map(renderBlock)}
                {attachments.filter((a) => a.section_id === s.id).map((a) => (
                  <AttachmentBlock
                    key={a.id} att={a}
                    index={numbering[a.id] || 1}
                    total={attachments.length}
                    sections={sectionsList} workspaceId={activeWorkspace.id} docId={id} canEdit={canEdit}
                    selected={selectedBlock === a.id} onSelect={setSelectedBlock}
                    onUpdate={updateAttachment} onMove={moveAttachment} onDelete={deleteAttachment}
                  />
                ))}
              </div>
            ))}
            {attachments.filter((a) => !a.section_id).length > 0 && (
              <div data-testid="unplaced-attachments">
                <h2 className="section">Attachments</h2>
                {attachments.filter((a) => !a.section_id).map((a) => (
                  <AttachmentBlock
                    key={a.id} att={a}
                    index={numbering[a.id] || 1}
                    total={attachments.length}
                    sections={sectionsList} workspaceId={activeWorkspace.id} docId={id} canEdit={canEdit}
                    selected={selectedBlock === a.id} onSelect={setSelectedBlock}
                    onUpdate={updateAttachment} onMove={moveAttachment} onDelete={deleteAttachment}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        <aside className="col-span-12 lg:col-span-3 border-l border-[#1E293B] bg-[#0A0F1F] overflow-y-auto p-4 fx-scrollbar space-y-4" data-testid="right-metadata">
          <div className="fx-panel-2 p-3">
            <div className="mono text-[10px] uppercase tracking-widest text-slate-500 mb-2 flex items-center justify-between">
              <span>Metadata</span>
              {canEdit && <button onClick={()=>setShowMeta(true)} className="text-cyan-300 hover:text-cyan-200 normal-case tracking-normal text-[11px]" data-testid="edit-metadata">Edit</button>}
            </div>
            <MetaRow k="Type" v={doc.document_type}/>
            <MetaRow k="Number" v={doc.document_number}/>
            <MetaRow k="Revision" v={doc.revision}/>
            <MetaRow k="Department" v={doc.metadata?.department}/>
            <MetaRow k="Site" v={doc.metadata?.site}/>
            <MetaRow k="Author" v={doc.metadata?.author}/>
            <MetaRow k="Reviewer" v={doc.metadata?.reviewer}/>
            <MetaRow k="Approver" v={doc.metadata?.approver}/>
            <MetaRow k="Effective" v={doc.metadata?.effective_date}/>
            {Object.entries(doc.metadata?.custom || {}).map(([k,v]) => <MetaRow key={k} k={k.replace(/_/g,' ')} v={String(v ?? '')}/>)}
          </div>

          {current && (
            <div className="fx-panel-2 p-3">
              <div className="mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">Selected section</div>
              <div className="text-sm text-slate-100 font-medium">{current.heading}</div>
              <div className="mt-1"><StatusBadge status={current.status || "complete"}/></div>
              {current.note && <div className="text-xs text-amber-300 mt-2">{current.note}</div>}
              {canEdit && (
                <div className="mt-3 flex flex-wrap gap-2">
                  <button onClick={startEdit} className="border border-white/10 hover:border-white/20 text-slate-200 text-xs px-2 py-1 rounded" data-testid="edit-section"><Save size={12} className="inline mr-1"/>Edit</button>
                  <div className="relative">
                    <button onClick={()=>setShowRegen(!showRegen)} className="border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10 text-xs px-2 py-1 rounded inline-flex items-center gap-1" data-testid="regen-section">{busy ? <Loader2 size={12} className="animate-spin"/> : <Wand2 size={12}/>} Regenerate <ChevronDown size={10}/></button>
                    {showRegen && (
                      <div className="absolute right-0 mt-1 fx-panel-2 rounded-lg shadow-xl z-20 w-48">
                        {regenPresets.map((p) => <button key={p.k} onClick={()=>regenerate(p.k)} className="w-full text-left px-3 py-2 text-xs text-slate-200 hover:bg-white/5" data-testid={`regen-${p.k}`}>{p.label}</button>)}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {editing && canEdit && (
                <div className="mt-3">
                  <textarea rows={8} value={editText} onChange={(e)=>setEditText(e.target.value)} className="w-full bg-[#050816] border border-[#1E293B] rounded p-2 text-xs text-slate-100 outline-none focus:border-cyan-400" data-testid="edit-textarea"/>
                  <div className="mt-2 flex justify-end gap-2">
                    <button onClick={()=>setEditing(false)} className="text-xs text-slate-400 hover:text-white px-2 py-1"><X size={12} className="inline mr-1"/>Cancel</button>
                    <button onClick={saveEdit} disabled={busy} className="fx-btn-primary text-white text-xs px-3 py-1 rounded disabled:opacity-50" data-testid="save-edit">Save</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {canEdit && (
            <div className="fx-panel-2 p-3" data-testid="page-setup">
              <div className="mono text-[10px] uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1"><LayoutTemplate size={12} className="text-cyan-300"/> Page setup</div>
              <label className="flex items-center justify-between py-1.5 text-xs cursor-pointer">
                <span className="text-slate-300">Cover page</span>
                <input type="checkbox" checked={coverMode === "standard"} onChange={(e)=>setPageSetting("cover_page", e.target.checked ? "standard" : "none")} className="accent-cyan-400" data-testid="toggle-cover"/>
              </label>
              <label className="flex items-center justify-between py-1.5 text-xs cursor-pointer">
                <span className="text-slate-300">Contents page</span>
                <input type="checkbox" checked={contentsMode === "auto"} onChange={(e)=>setPageSetting("contents_page", e.target.checked ? "auto" : "none")} className="accent-cyan-400" data-testid="toggle-contents"/>
              </label>
              <div className="text-[10px] text-slate-500 mt-1">Word builds the contents from headings on export and may ask to update the field on first open.</div>
            </div>
          )}

          <div className="fx-panel-2 p-3">
            <div className="mono text-[10px] uppercase tracking-widest text-slate-500 mb-2 flex items-center justify-between">
              <span className="flex items-center gap-1"><Paperclip size={12} className="text-cyan-300"/> Blocks</span>
              <span className="text-slate-500 normal-case tracking-normal">{attachments.length}</span>
            </div>
            {canEdit && (
              <div className="grid grid-cols-3 gap-1 mb-2">
                <button onClick={()=>setShowAttach("image")} className="border border-white/10 hover:border-cyan-500/40 hover:bg-cyan-500/5 text-slate-300 hover:text-cyan-300 text-[11px] py-1.5 rounded flex flex-col items-center gap-0.5" data-testid="add-image-btn"><ImageIcon size={12}/> Image</button>
                <button onClick={()=>setShowAttach("table")} className="border border-white/10 hover:border-cyan-500/40 hover:bg-cyan-500/5 text-slate-300 hover:text-cyan-300 text-[11px] py-1.5 rounded flex flex-col items-center gap-0.5" data-testid="add-table-btn"><TableIcon size={12}/> Table</button>
                <button onClick={()=>setShowAttach("chart")} className="border border-white/10 hover:border-cyan-500/40 hover:bg-cyan-500/5 text-slate-300 hover:text-cyan-300 text-[11px] py-1.5 rounded flex flex-col items-center gap-0.5" data-testid="add-chart-btn"><BarChart3 size={12}/> Chart</button>
              </div>
            )}
            {attachments.length === 0 ? (
              <div className="text-[11px] text-slate-500 italic">{canEdit ? "No blocks yet. Add images, tables or charts — place them in any section and they appear here and in the exported document." : "No blocks on this document."}</div>
            ) : (
              <div className="space-y-1.5">
                {attachments.map((a, i) => (
                  <div key={a.id} onClick={()=>setSelectedBlock(a.id)} className={`flex items-center gap-2 bg-[#050816] border rounded px-2 py-1.5 cursor-pointer ${selectedBlock===a.id ? "border-cyan-400/60" : "border-[#1E293B]"}`} data-testid={`att-${a.id}`}>
                    <span className="text-cyan-400/70 shrink-0"><TypeIcon type={a.type}/></span>
                    <div className="min-w-0 flex-1">
                      <div className="text-[11px] text-slate-200 truncate">{a.caption || `${a.type.charAt(0).toUpperCase()+a.type.slice(1)} ${numbering[a.id] || i+1}`}</div>
                      <div className="text-[10px] mono text-slate-500 uppercase truncate">{a.section_id ? (sectionsList.find(s=>s.id===a.section_id)?.heading || "section") : "end of document"}{a.type !== "table" ? ` · ${a.width_pct || 100}%` : ""}</div>
                    </div>
                    {canEdit && <>
                      <button onClick={()=>moveAttachment(a.id, -1)} disabled={i===0} className="text-slate-500 hover:text-cyan-300 disabled:opacity-30 p-0.5" data-testid={`att-up-${a.id}`}><ArrowUp size={12}/></button>
                      <button onClick={()=>moveAttachment(a.id, 1)} disabled={i===attachments.length-1} className="text-slate-500 hover:text-cyan-300 disabled:opacity-30 p-0.5" data-testid={`att-down-${a.id}`}><ArrowDown size={12}/></button>
                      <button onClick={()=>deleteAttachment(a.id)} className="text-slate-500 hover:text-red-400 p-0.5" data-testid={`att-del-${a.id}`}><Trash2 size={12}/></button>
                    </>}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="fx-panel-2 p-3">
            <div className="mono text-[10px] uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1"><AlertTriangle size={12} className="text-amber-400"/> Review items</div>
            {reviewItems.length === 0 ? (
              <div className="text-xs text-emerald-300">No open review items.</div>
            ) : (
              <div className="space-y-2">
                {reviewItems.map((r, i) => (
                  <div key={i} className="border-l-2 border-amber-500 bg-amber-500/5 pl-2 py-1.5" data-testid={`review-item-${r.section_id}`}>
                    <div className="text-xs text-amber-200">{r.message}</div>
                    {canEdit && (
                      <div className="mt-1 flex gap-2">
                        <button onClick={()=>{setActiveSection(r.section_id); startEdit();}} className="text-[10px] text-cyan-300 hover:text-cyan-200">Add info</button>
                        <button onClick={()=>resolveItem(r.section_id, "not_applicable")} className="text-[10px] text-slate-400 hover:text-slate-200" data-testid={`na-${r.section_id}`}>Not applicable</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </aside>
      </div>
      {showMeta && (
        <MetadataDialog doc={doc} template={template} workspaceId={activeWorkspace.id} onClose={()=>setShowMeta(false)} onSaved={(d)=>{ setDoc(d); setShowMeta(false); }}/>
      )}
      {showAttach && (
        <AttachmentDialog
          kind={showAttach}
          workspaceId={activeWorkspace.id}
          docId={id}
          onClose={()=>setShowAttach(null)}
          onAdded={(att)=>{ setDoc((d)=>({ ...d, attachments: [ ...(d.attachments||[]), att ] })); setShowAttach(null); toast.success("Attachment added"); }}
        />
      )}
    </div>
  );
}

function MetaRow({k, v}) {
  return <div className="flex justify-between py-1 text-xs"><span className="text-slate-500 capitalize">{k}</span><span className="text-slate-200 text-right ml-2 truncate max-w-[60%]">{v || "—"}</span></div>;
}

function MetadataDialog({ doc, template, workspaceId, onClose, onSaved }) {
  const [form, setForm] = React.useState({
    title: doc.title || "",
    document_number: doc.document_number || "",
    revision: doc.revision || "",
    department: doc.metadata?.department || "",
    site: doc.metadata?.site || "",
    author: doc.metadata?.author || "",
    reviewer: doc.metadata?.reviewer || "",
    approver: doc.metadata?.approver || "",
    effective_date: doc.metadata?.effective_date || "",
  });
  const [custom, setCustom] = React.useState({ ...(doc.metadata?.custom || {}) });
  const [busy, setBusy] = React.useState(false);
  const change = (k, v) => setForm({ ...form, [k]: v });
  const changeCustom = (k, v) => setCustom({ ...custom, [k]: v });

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        title: form.title, document_number: form.document_number, revision: form.revision,
        metadata: {
          department: form.department, site: form.site, author: form.author,
          reviewer: form.reviewer, approver: form.approver, effective_date: form.effective_date,
          custom,
        },
      };
      const { data } = await api.patch(`/workspaces/${workspaceId}/documents/${doc.id}`, payload);
      toast.success("Metadata saved");
      onSaved(data);
    } catch { toast.error("Could not save"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-40 p-4" onClick={onClose}>
      <div className="fx-panel p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto fx-scrollbar" onClick={(e)=>e.stopPropagation()} data-testid="metadata-dialog">
        <div className="flex items-center justify-between mb-4"><div className="font-display text-lg font-semibold">Edit metadata</div><button onClick={onClose} className="text-slate-500 hover:text-white"><X size={16}/></button></div>
        <div className="grid md:grid-cols-2 gap-3">
          <Fld label="Title" v={form.title} on={(v)=>change("title",v)} testid="md-title"/>
          <Fld label="Document number" v={form.document_number} on={(v)=>change("document_number",v)} testid="md-number"/>
          <Fld label="Revision" v={form.revision} on={(v)=>change("revision",v)} testid="md-revision"/>
          <Fld label="Effective date" type="date" v={form.effective_date} on={(v)=>change("effective_date",v)} testid="md-date"/>
          <Fld label="Department" v={form.department} on={(v)=>change("department",v)} testid="md-department"/>
          <Fld label="Site" v={form.site} on={(v)=>change("site",v)} testid="md-site"/>
          <Fld label="Author" v={form.author} on={(v)=>change("author",v)} testid="md-author"/>
          <Fld label="Reviewer" v={form.reviewer} on={(v)=>change("reviewer",v)} testid="md-reviewer"/>
          <Fld label="Approver" v={form.approver} on={(v)=>change("approver",v)} testid="md-approver"/>
        </div>
        {(template?.metadata_fields || []).length > 0 && (
          <div className="mt-4 pt-4 border-t border-[#1E293B]">
            <div className="mono text-[10px] uppercase tracking-widest text-cyan-300 mb-2">Template fields</div>
            <div className="grid md:grid-cols-2 gap-3">
              {template.metadata_fields.map((f) => (
                <label key={f.key} className="block">
                  <div className="text-xs text-slate-400 mb-1">{f.label}{f.required && <span className="text-red-400 ml-1">*</span>}</div>
                  {f.type === "dropdown" ? (
                    <select value={custom[f.key] || ""} onChange={(e)=>changeCustom(f.key, e.target.value)} className="w-full bg-[#0D1224] border border-[#1E293B] rounded-lg px-3 py-2 text-sm text-slate-100" data-testid={`md-custom-${f.key}`}><option value="">Select…</option>{(f.options||[]).map(o => <option key={o} value={o}>{o}</option>)}</select>
                  ) : f.type === "toggle" ? (
                    <input type="checkbox" checked={!!custom[f.key]} onChange={(e)=>changeCustom(f.key, e.target.checked)} className="accent-cyan-400" data-testid={`md-custom-${f.key}`}/>
                  ) : (
                    <input type={f.type==="number"?"number":f.type==="date"?"date":"text"} value={custom[f.key] ?? ""} onChange={(e)=>changeCustom(f.key, f.type==="number" ? (e.target.value===""?"":Number(e.target.value)) : e.target.value)} className="w-full bg-[#0D1224] border border-[#1E293B] rounded-lg px-3 py-2 text-sm text-slate-100" data-testid={`md-custom-${f.key}`}/>
                  )}
                </label>
              ))}
            </div>
          </div>
        )}
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="border border-white/10 hover:border-white/20 text-slate-200 text-sm px-4 py-2 rounded-lg">Cancel</button>
          <button onClick={save} disabled={busy} className="fx-btn-primary text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-50" data-testid="md-save">{busy?"Saving…":"Save metadata"}</button>
        </div>
      </div>
    </div>
  );
}

function Fld({label, v, on, type="text", testid}) {
  return (<label className="block"><div className="text-xs text-slate-400 mb-1">{label}</div><input type={type} value={v} onChange={(e)=>on(e.target.value)} className="w-full bg-[#0D1224] border border-[#1E293B] rounded-lg px-3 py-2 text-sm text-slate-100 focus:border-cyan-400 outline-none" data-testid={testid}/></label>);
}


function AttachmentDialog({ kind, workspaceId, docId, onClose, onAdded }) {
  const [caption, setCaption] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  // image / chart-upload
  const [file, setFile] = React.useState(null);
  // table
  const [columns, setColumns] = React.useState(["Column A", "Column B"]);
  const [rows, setRows] = React.useState([["", ""], ["", ""]]);
  // chart
  const [chartMode, setChartMode] = React.useState("generated"); // generated | upload
  const [chartType, setChartType] = React.useState("bar");
  const [chartData, setChartData] = React.useState([{ label: "Q1", value: 10 }, { label: "Q2", value: 25 }, { label: "Q3", value: 18 }]);

  const setCell = (ri, ci, v) => {
    const next = rows.map((r, i) => r.map((c, j) => (i === ri && j === ci ? v : c)));
    setRows(next);
  };
  const addRow = () => setRows([...rows, Array(columns.length).fill("")]);
  const removeRow = (ri) => setRows(rows.filter((_, i) => i !== ri));
  const addCol = () => { setColumns([...columns, `Column ${String.fromCharCode(65 + columns.length)}`]); setRows(rows.map((r) => [...r, ""])); };
  const removeCol = (ci) => { setColumns(columns.filter((_, i) => i !== ci)); setRows(rows.map((r) => r.filter((_, i) => i !== ci))); };
  const renameCol = (ci, v) => setColumns(columns.map((c, i) => (i === ci ? v : c)));

  const addChartRow = () => setChartData([...chartData, { label: "", value: 0 }]);
  const removeChartRow = (i) => setChartData(chartData.filter((_, j) => j !== i));
  const setChartRow = (i, k, v) => setChartData(chartData.map((r, j) => (i === j ? { ...r, [k]: v } : r)));

  const submit = async () => {
    setBusy(true);
    try {
      let att;
      if (kind === "image") {
        if (!file) { toast.error("Choose an image"); setBusy(false); return; }
        const fd = new FormData(); fd.append("file", file); fd.append("caption", caption);
        const { data } = await api.post(`/workspaces/${workspaceId}/documents/${docId}/attachments/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        att = data;
      } else if (kind === "table") {
        const cleanCols = columns.map((c) => (c || "").trim()).filter(Boolean);
        if (cleanCols.length === 0) { toast.error("Add at least one column"); setBusy(false); return; }
        const cleanRows = rows.map((r) => r.slice(0, cleanCols.length).map((v) => v ?? ""));
        const { data } = await api.post(`/workspaces/${workspaceId}/documents/${docId}/attachments/table`, { caption, columns: cleanCols, rows: cleanRows });
        att = data;
      } else if (kind === "chart") {
        if (chartMode === "upload") {
          if (!file) { toast.error("Choose a chart image"); setBusy(false); return; }
          const fd = new FormData(); fd.append("file", file); fd.append("caption", caption);
          const { data } = await api.post(`/workspaces/${workspaceId}/documents/${docId}/attachments/chart-image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          att = data;
        } else {
          const labels = chartData.map((r) => (r.label || "").trim()).filter(Boolean);
          const values = chartData.filter((r) => (r.label || "").trim()).map((r) => Number(r.value) || 0);
          if (labels.length === 0) { toast.error("Add at least one data point"); setBusy(false); return; }
          const { data } = await api.post(`/workspaces/${workspaceId}/documents/${docId}/attachments/chart`, { caption, chart_type: chartType, labels, values });
          att = data;
        }
      }
      onAdded(att);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add attachment");
    } finally { setBusy(false); }
  };

  const inputCls = "w-full bg-[#0D1224] border border-[#1E293B] rounded-lg px-3 py-2 text-sm text-slate-100 focus:border-cyan-400 outline-none";
  const title = kind === "image" ? "Add image" : kind === "table" ? "Add table" : "Add chart";

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-40 p-4" onClick={onClose}>
      <div className="fx-panel p-6 w-full max-w-3xl max-h-[92vh] overflow-y-auto fx-scrollbar" onClick={(e)=>e.stopPropagation()} data-testid={`attachment-dialog-${kind}`}>
        <div className="flex items-center justify-between mb-4"><div className="font-display text-lg font-semibold flex items-center gap-2">{kind === "image" ? <ImageIcon size={16} className="text-cyan-300"/> : kind === "table" ? <TableIcon size={16} className="text-cyan-300"/> : <BarChart3 size={16} className="text-cyan-300"/>}{title}</div><button onClick={onClose} className="text-slate-500 hover:text-white" data-testid="attach-close"><X size={16}/></button></div>

        <label className="block mb-4"><div className="text-xs text-slate-400 mb-1">Label / caption</div><input value={caption} onChange={(e)=>setCaption(e.target.value)} placeholder={kind==="image" ? "e.g. Site plan overview" : kind==="table" ? "e.g. Isolation points" : "e.g. Q1–Q4 defect trend"} className={inputCls} data-testid="attach-caption"/></label>

        {kind === "image" && (
          <FileDropInline onFile={setFile} file={file} testid="image-file"/>
        )}

        {kind === "table" && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-xs text-slate-400">Columns and rows</div>
              <div className="flex gap-2">
                <button onClick={addCol} className="text-xs text-cyan-300 hover:text-cyan-200 inline-flex items-center gap-1" data-testid="add-col-btn"><Plus size={12}/> Column</button>
                <button onClick={addRow} className="text-xs text-cyan-300 hover:text-cyan-200 inline-flex items-center gap-1" data-testid="add-row-btn"><Plus size={12}/> Row</button>
              </div>
            </div>
            <div className="overflow-x-auto border border-[#1E293B] rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-[#0D1224]">
                  <tr>
                    {columns.map((c, ci) => (
                      <th key={ci} className="p-1.5 border-b border-r border-[#1E293B]">
                        <div className="flex items-center gap-1">
                          <input value={c} onChange={(e)=>renameCol(ci, e.target.value)} className="bg-transparent text-slate-100 text-xs font-semibold outline-none border border-transparent focus:border-cyan-400 rounded px-1 py-0.5 w-full" data-testid={`col-name-${ci}`}/>
                          {columns.length > 1 && <button onClick={()=>removeCol(ci)} className="text-slate-500 hover:text-red-400" data-testid={`del-col-${ci}`}><X size={11}/></button>}
                        </div>
                      </th>
                    ))}
                    <th className="w-6 border-b border-[#1E293B]"/>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, ri) => (
                    <tr key={ri}>
                      {columns.map((_, ci) => (
                        <td key={ci} className="border-b border-r border-[#1E293B] p-0">
                          <input value={r[ci] || ""} onChange={(e)=>setCell(ri, ci, e.target.value)} className="w-full bg-[#050816] text-xs text-slate-100 px-2 py-1.5 outline-none focus:bg-cyan-500/5" data-testid={`cell-${ri}-${ci}`}/>
                        </td>
                      ))}
                      <td className="border-b border-[#1E293B] text-center">
                        {rows.length > 1 && <button onClick={()=>removeRow(ri)} className="text-slate-500 hover:text-red-400 p-1" data-testid={`del-row-${ri}`}><X size={11}/></button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {kind === "chart" && (
          <div className="space-y-3">
            <div className="flex gap-2 flex-wrap">
              <button onClick={()=>setChartMode("generated")} className={`px-3 py-1.5 rounded-lg text-xs border ${chartMode==="generated" ? "border-cyan-400/60 bg-cyan-500/10 text-cyan-200" : "border-white/10 text-slate-400"}`} data-testid="chart-mode-generated">Build with data</button>
              <button onClick={()=>setChartMode("upload")} className={`px-3 py-1.5 rounded-lg text-xs border ${chartMode==="upload" ? "border-cyan-400/60 bg-cyan-500/10 text-cyan-200" : "border-white/10 text-slate-400"}`} data-testid="chart-mode-upload">Upload image</button>
            </div>
            {chartMode === "upload" ? (
              <FileDropInline onFile={setFile} file={file} testid="chart-file"/>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="text-xs text-slate-400">Chart type</div>
                  {["bar","line","pie"].map((t) => (
                    <button key={t} onClick={()=>setChartType(t)} className={`px-3 py-1 rounded text-xs border capitalize ${chartType===t ? "border-cyan-400/60 bg-cyan-500/10 text-cyan-200" : "border-white/10 text-slate-400 hover:text-slate-200"}`} data-testid={`chart-type-${t}`}>{t}</button>
                  ))}
                </div>
                <div className="border border-[#1E293B] rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-[#0D1224]"><tr>
                      <th className="text-left text-xs text-slate-400 px-2 py-1.5 border-b border-[#1E293B]">Label</th>
                      <th className="text-left text-xs text-slate-400 px-2 py-1.5 border-b border-[#1E293B]">Value</th>
                      <th className="w-8 border-b border-[#1E293B]"/>
                    </tr></thead>
                    <tbody>
                      {chartData.map((r, i) => (
                        <tr key={i}>
                          <td className="border-b border-[#1E293B] p-0"><input value={r.label} onChange={(e)=>setChartRow(i, "label", e.target.value)} className="w-full bg-[#050816] text-xs text-slate-100 px-2 py-1.5 outline-none focus:bg-cyan-500/5" data-testid={`chart-label-${i}`}/></td>
                          <td className="border-b border-[#1E293B] p-0"><input type="number" value={r.value} onChange={(e)=>setChartRow(i, "value", e.target.value)} className="w-full bg-[#050816] text-xs text-slate-100 px-2 py-1.5 outline-none focus:bg-cyan-500/5" data-testid={`chart-value-${i}`}/></td>
                          <td className="border-b border-[#1E293B] text-center">{chartData.length > 1 && <button onClick={()=>removeChartRow(i)} className="text-slate-500 hover:text-red-400 p-1" data-testid={`chart-del-${i}`}><X size={11}/></button>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button onClick={addChartRow} className="text-xs text-cyan-300 hover:text-cyan-200 inline-flex items-center gap-1" data-testid="chart-add-row"><Plus size={12}/> Add data point</button>
              </div>
            )}
          </div>
        )}

        <div className="mt-6 flex justify-end gap-2 pt-4 border-t border-[#1E293B]">
          <button onClick={onClose} className="border border-white/10 hover:border-white/20 text-slate-200 text-sm px-4 py-2 rounded-lg">Cancel</button>
          <button onClick={submit} disabled={busy} className="fx-btn-primary text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-50 inline-flex items-center gap-2" data-testid="attach-save">{busy ? <Loader2 size={14} className="animate-spin"/> : <Plus size={14}/>} Add to document</button>
        </div>
      </div>
    </div>
  );
}

function FileDropInline({ onFile, file, testid }) {
  const [drag, setDrag] = React.useState(false);
  return (
    <label
      onDragOver={(e)=>{e.preventDefault(); setDrag(true);}}
      onDragLeave={()=>setDrag(false)}
      onDrop={(e)=>{e.preventDefault(); setDrag(false); const f = e.dataTransfer.files?.[0]; if (f) onFile(f);}}
      className={`block border-2 border-dashed rounded-xl p-8 text-center transition cursor-pointer ${drag ? "border-cyan-400 bg-cyan-500/5" : "border-[#1E293B] hover:border-slate-600"}`}
      data-testid={testid}
    >
      <UploadCloud size={22} className="mx-auto text-cyan-300"/>
      <div className="mt-2 text-sm text-slate-200">{file ? file.name : "Drag an image here or click to upload"}</div>
      <div className="text-[11px] text-slate-500 mt-1">PNG · JPG · GIF · BMP</div>
      <input type="file" accept="image/png,image/jpeg,image/gif,image/bmp" className="hidden" onChange={(e)=>{ const f = e.target.files?.[0]; if (f) onFile(f); }} data-testid={`${testid}-input`}/>
    </label>
  );
}
